import React, { useState, useCallback, useRef } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import BrandMark from './BrandMark';
import './ScreenshotFrame.css';

/**
 * ScreenshotFrame — displays product screenshots in a styled browser frame
 * with WebP/PNG fallback and lightbox on click.
 *
 * Props:
 *   src       — image path WITHOUT extension (e.g. "/images/screenshots/command-center")
 *   alt       — accessibility text (required)
 *   caption   — optional caption below the frame
 *   size      — "full" | "large" | "medium" | "thumbnail" (default "large")
 *   lazy      — boolean, default true (hero images should pass false)
 *   className — additional CSS class
 *   retina    — boolean, default false. Opt-in density srcSet: emits `1x`/`2x`
 *               descriptors pointing at `${src}.webp`/`${src}@2x.webp` (and the PNG
 *               fallback likewise). ONLY set true when the `@2x` twins exist on disk
 *               (PRD-171C WP12 re-captures); otherwise the `2x` descriptor 404s.
 */
export default function ScreenshotFrame({
  src,
  alt,
  caption,
  size = 'large',
  lazy = true,
  className = '',
  retina = false,
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const triggerRef = useRef(null);

  const handleOpen = useCallback(() => setLightboxOpen(true), []);
  const handleClose = useCallback(() => {
    setLightboxOpen(false);
    // Return focus to the trigger so keyboard users are not dumped at the top of
    // the document when the lightbox closes (PRD-171B WP11).
    triggerRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Suppress Space's default page-scroll on the <figure role="button"> (WP11).
      e.preventDefault();
      handleOpen();
    }
  }, [handleOpen]);

  const webpSrc = `${src}.webp`;
  const pngSrc = `${src}.png`;
  // Additive density descriptors — only when opted in AND the @2x twins exist.
  // retina=false keeps the emitted markup byte-identical to the 20+ existing call sites.
  const webpSrcSet = retina ? `${webpSrc} 1x, ${src}@2x.webp 2x` : webpSrc;
  const imgSrcSet = retina ? `${pngSrc} 1x, ${src}@2x.png 2x` : undefined;

  return (
    <>
      <figure
        ref={triggerRef}
        className={`screenshot-frame screenshot-frame--${size} ${className}`.trim()}
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-label={`View full size: ${alt}`}
      >
        {/* Browser chrome */}
        <div className="screenshot-frame__chrome">
          <BrandMark size={16} className="screenshot-frame__brand" />
          <div className="screenshot-frame__url-bar">
            <span className="screenshot-frame__url-text">driftalarm.com</span>
          </div>
        </div>

        {/* Image with WebP fallback */}
        <div className="screenshot-frame__image-wrapper">
          <picture>
            <source srcSet={webpSrcSet} type="image/webp" />
            <img
              src={pngSrc}
              {...(imgSrcSet ? { srcSet: imgSrcSet } : {})}
              alt={alt}
              className="screenshot-frame__image"
              loading={lazy ? 'lazy' : 'eager'}
              decoding="async"
            />
          </picture>
          <div className="screenshot-frame__zoom-hint">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
              <line x1="11" y1="11" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="5" y1="7" x2="9" y2="7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
              <line x1="7" y1="5" x2="7" y2="9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </div>
        </div>

        {caption && (
          <figcaption className="screenshot-frame__caption">{caption}</figcaption>
        )}
      </figure>

      {lightboxOpen && (
        <Lightbox
          open={lightboxOpen}
          close={handleClose}
          slides={[{ src: pngSrc, alt }]}
          render={{
            buttonPrev: () => null,
            buttonNext: () => null,
          }}
          styles={{
            container: { backgroundColor: 'rgba(0, 0, 0, 0.92)' },
          }}
        />
      )}
    </>
  );
}
